import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ToastType } from "src/components/shared-components/enum/enumMisc";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { AccountingRequest } from "src/model/class/general classes/accounting-entities";
import { GenericTableAnagraphAnagraph } from "src/model/class/general classes/generic-table-anagraph-anagraph";
import { ReportHTML } from "src/model/class/general classes/report-html";
import { AccountingExportFileType } from "src/model/enum/enumAccounting";
import { AccountingService } from "src/service/accounting.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";
import { ReporthtmlService } from "src/service/reporthtml.service";
import { ToastService } from "src/service/toast.service";
import { DateValidators } from "src/validators/date-validators";
import { AccountingDetailComponent } from "../accounting-detail/accounting-detail.component";

@Component({
    selector: "app-accounting-generate",
    templateUrl: "./accounting-generate.component.html",
    styleUrls: ["./accounting-generate.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AccountingGenerateComponent
  extends GenericTableAnagraphAnagraph
  implements OnInit, OnDestroy {
  private _logContext: string = "Accounting-generate.component";
  private _networkServiceSubscription: Subscription = null;
  private _translateSubscription: Subscription = null;
  public toolBarColor: string = "primary";
  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public accountingGenerationFormGroup: UntypedFormGroup;
  public accountingExportFileType = AccountingExportFileType;

  public firstStartValue: Date;
  public firstEndValue: Date;

  private lblErrorDate = "";

  public startDate: Date;
  public endDate: Date;
  public reportsList: ReportHTML[];

  private templateConditionalValidator = [];

  constructor (
    private _networkService: NetworkService,
    private _translateService: TranslateService,
    private _modalController: ModalController,
    private _formBuilder: UntypedFormBuilder,
    private _reportHtmlService: ReporthtmlService,
    private _toastService: ToastService,
    private _accountingEntityService: AccountingService,
    private _loggerService: LoggerService
  ) {
    super();
    const firstDate = moment().startOf("month").format("YYYY-MM-DD");
    const endDate = moment().format("YYYY-MM-DD");
    this.accountingGenerationFormGroup = this._formBuilder.group(
      {
        startDate: new UntypedFormControl(firstDate, [Validators.required]),
        endDate: new UntypedFormControl(endDate, [Validators.required]),
        kind: new UntypedFormControl(this.accountingExportFileType.preview),
        template: new UntypedFormControl(0, this.templateConditionalValidator),
        fromLastPayment: new UntypedFormControl(false),
      },
      { validator: DateValidators.dateLessThan("startDate", "endDate") }
    );

    _reportHtmlService.Init();

    this.accountingGenerationFormGroup.valueChanges.subscribe((event) => {
      if (this.accountingGenerationFormGroup.errors != null) {
        this._toastService.showToast(ToastType.ERROR, this.lblErrorDate, 250);
      }
      if (event.fromLastPayment) {
        let dateMin: Date;
        this._accountingEntityService.accountingEntitiesList.subscribe(
          (entities) => {
            const ids: number[] = (this.CurrentObject as string)
              .split(",")
              .map((x) => parseInt(x));
            const dateToUpdate = moment(
              entities.find((entity) => entity.id == ids[0]).dateLastPayment
            ).format("YYYY-MM-DD");
            if (event.startDate !== dateToUpdate) {
              this.accountingGenerationFormGroup.patchValue({
                startDate: dateToUpdate,
              });
            }
          }
        );
      }
    });

    this.firstStartValue = moment().toDate();
    this.firstEndValue = moment().toDate();
  }

  ngOnInit() {
    this.subscribeNetworkServiceData();
    this.loadTranslation();
    this.subscribeTranslationEvent();

    this._reportHtmlService.reportHTMLlist
      .pipe(filter((report) => report !== null))
      .subscribe((reportList) => {
        this.reportsList = reportList.filter((report) => {
          return report.kind === 0;
        });
      });
  }

  ngOnDestroy() {
    this._networkServiceSubscription.unsubscribe();
    this._translateSubscription.unsubscribe();
  }

  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;
          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.timeEndGreaterThanStart"])
      .subscribe((translation) => {
        this.lblErrorDate = translation["dictionary.timeEndGreaterThanStart"];
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * On confirm preview return period
   */
  public onConfirmPreview() {
    const listEntities = this.CurrentObject;

    let requestObject: AccountingRequest = {
      startDate: this.accountingGenerationFormGroup.value["startDate"],
      endDate: this.accountingGenerationFormGroup.value["endDate"],
      listEntities: listEntities,
      kindReportHTMPPDF: AccountingExportFileType.preview,
    };

    // change date format to be correctly handled by api
    requestObject.startDate = moment(
      requestObject.startDate,
      "YYYY-MM-DD"
    ).format("DD-MM-YYYY 00:00:00");
    requestObject.endDate = moment(requestObject.endDate, "YYYY-MM-DD").format(
      "DD-MM-YYYY 23:59:59"
    );

    this._accountingEntityService
      .GeneratePayment(requestObject)
      .then(async (pathOfFileExported) => {
        const modal = await this._modalController.create({
          component: AccountingDetailComponent,
          cssClass: "modal-bigger",
          componentProps: {
            showConfirmButton: true,
            idsAccountingNotes: pathOfFileExported.idNotesGenerated,
          },
        });
        modal.present();
      })
      .catch((error) => {
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          "onConfirmPreview()",
          "Error generating payment: " + error
        );
      });
  }

  /**
   * When the kind combo is changed reset the validator to remove or add the validation on the template html
   */
  public onSelectExportKindChange() {
    if (
      this.accountingGenerationFormGroup.value["kind"] ==
      this.accountingExportFileType.pdf
    ) {
      this.templateConditionalValidator = [Validators.min(1)];
    } else {
      this.templateConditionalValidator = [];
    }
    this.accountingGenerationFormGroup
      .get("template")
      .setValidators(this.templateConditionalValidator);
    Object.keys(this.accountingGenerationFormGroup.controls).forEach((key) => {
      this.accountingGenerationFormGroup.get(key).setErrors(null);
    });
    this.accountingGenerationFormGroup.updateValueAndValidity();
  }
}
